import React, { useState } from 'react';
import { X, Film, Copy, Check, ThumbsUp, ThumbsDown } from 'lucide-react';
import { FlowingSphere } from '@/components/FlowingSphere';
import { AttachmentItem } from '@/components/agent/types';
import { useTypewriter, renderFormattedMessage, ThinkingIndicator } from '@/components/agent/components/ProcessLogPanel';
import { useReferenceStore } from '@/stores/referenceStore';

export const ReferenceThumbnails: React.FC<{
  attachments?: AttachmentItem[];
  onRemoveAttachment?: (index: number) => void;
}> = ({ attachments, onRemoveAttachment }) => {
  const referencedNodes = useReferenceStore((s) => s.referencedNodes);
  const removeReference = useReferenceStore((s) => s.removeReference);
  const hasRefs = referencedNodes.length > 0;
  const hasAtts = attachments && attachments.length > 0;

  // P2-6: 图片加载失败追踪
  const [failedRef, setFailedRef] = useState<Set<string>>(new Set());
  const [failedAtt, setFailedAtt] = useState<Set<number>>(new Set());

  if (!hasRefs && !hasAtts) return null;

  return (
    <div className="flex items-center gap-1.5 pb-2 mb-2 border-b border-white/10">
      <div className="flex items-center gap-1.5 flex-1 overflow-x-auto custom-scrollbar">
        {referencedNodes.map((ref) => {
          const hasFailed = failedRef.has(ref.id);
          return (
            <div key={ref.id} className="relative group shrink-0 rounded-md overflow-hidden border border-white/10 hover:border-white/30 transition-all" style={{ width: 36, height: 36 }}>
              {ref.thumbnailUrl && !hasFailed ? (
                <img
                  src={ref.thumbnailUrl}
                  className="w-full h-full object-cover"
                  draggable={false}
                  alt="参考图"
                  onError={() => setFailedRef(prev => { const n = new Set(prev); n.add(ref.id); return n; })}
                />
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                  <span className="text-[9px] text-slate-500">{ref.node.title?.slice(0, 2) || '节点'}</span>
                </div>
              )}
              <button onClick={() => removeReference(ref.id)} className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-black/60 text-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80">
                <X size={8} />
              </button>
            </div>
          );
        })}
        {attachments?.map((item, idx) => {
          const hasFailed = failedAtt.has(idx);
          return (
            <div key={`att-${idx}`} className="relative group shrink-0 rounded-md overflow-hidden border border-white/10 hover:border-white/30 transition-all" style={{ width: 36, height: 36 }}>
              {item.thumbnailUrl && !hasFailed ? (
                item.file?.type?.startsWith('video/') ? (
                  <div className="relative w-full h-full">
                    <video src={item.thumbnailUrl} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Film size={10} className="text-white/80" />
                    </div>
                  </div>
                ) : (
                  <img
                    src={item.thumbnailUrl}
                    className="w-full h-full object-cover"
                    draggable={false}
                    alt={item.file?.name || '附件'}
                    onError={() => setFailedAtt(prev => { const n = new Set(prev); n.add(idx); return n; })}
                  />
                )
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                  <span className="text-[9px] text-slate-500">{item.file?.name?.split('.').pop()?.slice(0, 3) || '?'}</span>
                </div>
              )}
              {onRemoveAttachment && (
                <button onClick={() => onRemoveAttachment(idx)} className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-black/60 text-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80">
                  <X size={8} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const StreamingMessage: React.FC<{
  text: string;
}> = ({ text }) => {
  return (
    <div className="text-[12px] leading-6 text-gray-300 select-text cursor-text">
      {renderFormattedMessage(text, true)}
      <span className="inline-block w-[2px] h-[12px] bg-cyan-400 ml-0.5 animate-pulse" />
    </div>
  );
};

interface AIReplyBlockProps {
  content: React.ReactNode;
  isStreaming?: boolean;
  thinkingSeconds?: number;
  isThinking?: boolean;
  onCopy?: () => void;
  onRate?: (type: 'up' | 'down') => void;
  showActions?: boolean;
}

export const AIReplyBlock: React.FC<AIReplyBlockProps> = ({
  content, isStreaming = false, thinkingSeconds, isThinking = false, onCopy, onRate, showActions = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [rated, setRated] = useState<'up' | 'down' | null>(null);

  const handleCopy = () => {
    if (onCopy) { onCopy(); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };
  const handleRate = (type: 'up' | 'down') => {
    if (onRate) { onRate(type); setRated(type); }
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex-shrink-0"><FlowingSphere size={18} isFlowing={isStreaming || isThinking} /></div>
        <div className="flex-1 min-w-0">{content}</div>
      </div>
      {isThinking && thinkingSeconds !== undefined && (
        <div className="ml-[26px]"><ThinkingIndicator seconds={thinkingSeconds} /></div>
      )}
      {showActions && (
        <div className="flex items-center gap-1 ml-[26px]">
          <button onClick={handleCopy} className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all" title="复制">
            {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            <span>{copied ? '已复制' : '复制'}</span>
          </button>
          <div className="w-px h-3 bg-white/10" />
          <button onClick={() => handleRate('up')} className={`flex items-center justify-center w-6 h-6 rounded-md transition-all ${rated === 'up' ? 'text-cyan-400 bg-cyan-400/10' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`} title="有用">
            <ThumbsUp size={12} />
          </button>
          <button onClick={() => handleRate('down')} className={`flex items-center justify-center w-6 h-6 rounded-md transition-all ${rated === 'down' ? 'text-rose-400 bg-rose-400/10' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`} title="无用">
            <ThumbsDown size={12} />
          </button>
        </div>
      )}
    </div>
  );
};
